import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, FileCheck, Share2, Printer, Calculator, Ruler, Repeat, X } from "lucide-react";

const freqLabels = { one_time: "One-time", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };
const statusStyle = { draft: "bg-muted text-muted-foreground", sent: "bg-accent/15 text-accent", accepted: "bg-primary/10 text-primary", declined: "bg-destructive/10 text-destructive" };

const computeQty = (mt, d) => {
  const L = Number(d.length) || 0, W = Number(d.width) || 0, D = Number(d.depth) || 0, Q = Number(d.quantity) || 0;
  if (mt === "area") return L * W;
  if (mt === "volume") return (L * W * D) / 324; // depth in inches -> cubic yards
  if (mt === "linear") return L;
  return Q || 1;
};
const detailStr = (mt, d, qty, unit) => {
  if (mt === "area") return `${d.length || 0}ft × ${d.width || 0}ft = ${qty.toFixed(0)} ${unit}`;
  if (mt === "volume") return `${d.length || 0}ft × ${d.width || 0}ft × ${d.depth || 0}in = ${qty.toFixed(2)} ${unit}`;
  if (mt === "linear") return `${d.length || 0} ${unit}`;
  return `${qty} ${unit}`;
};

export default function QuoteBuilder() {
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [config, setConfig] = useState({ one_time: 1, weekly: 1, biweekly: 1, monthly: 1 });

  const [customerId, setCustomerId] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]);

  // Add-service dialog
  const [open, setOpen] = useState(false);
  const [svcId, setSvcId] = useState("");
  const [variantIdx, setVariantIdx] = useState(0);
  const [dims, setDims] = useState({ length: "", width: "", depth: "", quantity: 1 });
  const [chosenAddons, setChosenAddons] = useState({});

  const loadQuotes = () => api.get("/quotes").then((r) => setQuotes(r.data));
  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data.filter((c) => c.type === "service")));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/quote-config").then((r) => setConfig(r.data.frequency_multipliers || config));
    loadQuotes();
    // eslint-disable-next-line
  }, []);

  const svc = catalog.find((c) => c.id === svcId);
  const openAdd = () => { setSvcId(""); setVariantIdx(0); setDims({ length: "", width: "", depth: "", quantity: 1 }); setChosenAddons({}); setOpen(true); };

  const selectService = (id) => {
    const s = catalog.find((c) => c.id === id);
    setSvcId(id); setVariantIdx(0);
    setDims({ length: "", width: "", depth: "", quantity: 1 }); setChosenAddons({});
  };

  const activeVariant = svc && (svc.variants || []).length ? svc.variants[variantIdx] : null;
  const unitPrice = activeVariant ? Number(activeVariant.unit_price) : (svc ? Number(svc.unit_price) : 0);
  const unitCost = activeVariant ? Number(activeVariant.unit_cost) : (svc ? Number(svc.unit_cost) : 0);
  const qty = svc ? computeQty(svc.measure_type, dims) : 0;
  const addonList = svc ? (svc.addons || []).filter((_, i) => chosenAddons[i]) : [];
  const svcLineTotal = qty * unitPrice;
  const addonsTotal = addonList.reduce((a, x) => a + Number(x.price || 0), 0);
  const previewTotal = svcLineTotal + addonsTotal;

  const addToQuote = () => {
    if (!svc) return toast.error("Pick a service");
    const label = activeVariant ? `${svc.name} — ${activeVariant.label}` : svc.name;
    const detail = detailStr(svc.measure_type, dims, qty, svc.unit);
    const newLines = [{
      item_id: svc.id, description: `${label} (${detail})`, quantity: Number(qty.toFixed(3)),
      unit_price: unitPrice, unit_cost: unitCost, is_addon: false,
      variant: activeVariant ? activeVariant.label : "", measure_type: svc.measure_type, unit_label: svc.unit,
      length: Number(dims.length) || 0, width: Number(dims.width) || 0, depth: Number(dims.depth) || 0, addons: addonList,
    }];
    addonList.forEach((x) => newLines.push({
      item_id: svc.id, description: `+ ${x.label} (${svc.name})`, quantity: 1,
      unit_price: Number(x.price) || 0, unit_cost: Number(x.cost) || 0, is_addon: true,
      variant: "", measure_type: "flat", unit_label: "", addons: [],
    }));
    setLines((l) => [...l, ...newLines]);
    setOpen(false);
    toast.success("Added to quote");
  };

  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const mult = Number(config[frequency]) || 1;
  const subtotal = lines.reduce((a, l) => a + l.quantity * l.unit_price, 0);
  const adjusted = subtotal * mult;
  const cost = lines.reduce((a, l) => a + l.quantity * l.unit_cost, 0) * mult;
  const tax = adjusted * (Number(taxRate) || 0) / 100;
  const total = adjusted + tax;
  const profit = adjusted - cost;

  const reset = () => { setLines([]); setFrequency("one_time"); setTaxRate(0); setNotes(""); setCustomerId(""); };

  const save = async () => {
    if (lines.length === 0) return toast.error("Add at least one service");
    const c = customers.find((x) => x.id === customerId);
    try {
      await api.post("/quotes", {
        customer_id: customerId, customer_name: c ? c.name : "", customer_email: c ? c.email : "",
        frequency, lines, tax_rate: Number(taxRate) || 0, status: "draft", notes,
      });
      toast.success("Quote saved"); reset(); loadQuotes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const share = async (id) => {
    try {
      const { data } = await api.get(`/quotes/${id}/share`);
      const url = `${window.location.origin}/q/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Customer link copied"); } catch { toast.success(url); }
      loadQuotes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openPrint = async (id) => {
    const { data } = await api.get(`/quotes/${id}/share`);
    window.open(`${window.location.origin}/q/${data.token}?print=1`, "_blank");
    loadQuotes();
  };
  const convert = async (id) => { try { await api.post(`/quotes/${id}/convert-estimate`); toast.success("Converted to estimate"); loadQuotes(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (id) => { await api.delete(`/quotes/${id}`); toast.success("Quote deleted"); loadQuotes(); };

  return (
    <div className="space-y-8" data-testid="quotebuilder-page">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-border rounded-lg bg-card">
            <div className="overline text-secondary mb-3">Step 1 — Customer & schedule</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>Customer (optional)</Label>
                <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5" /> Frequency</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="quote-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(freqLabels).map((f) => <SelectItem key={f} value={f}>{freqLabels[f]} (×{config[f] ?? 1})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tax Rate (%)</Label><Input data-testid="quote-tax-input" type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} /></div>
            </div>
          </Card>

          <Card className="p-6 border-border rounded-lg bg-card">
            <div className="flex items-center justify-between mb-4">
              <div><div className="overline text-secondary">Step 2 — Line items</div><p className="text-xs text-muted-foreground mt-0.5">Add each service; pick options and enter measurements.</p></div>
              <Button data-testid="add-service-btn" onClick={openAdd} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"><Plus className="h-4 w-4 mr-1.5" /> Add Service</Button>
            </div>
            <div className="space-y-2">
              {lines.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">No line items yet. Click "Add Service" to start.</p> : lines.map((l, i) => (
                <div key={i} data-testid="quote-line" className={`flex items-center gap-3 p-3 rounded-md border border-border ${l.is_addon ? "bg-muted/30 ml-6" : "bg-card"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{l.description}</div>
                    {!l.is_addon && <div className="text-xs text-muted-foreground">{l.quantity} {l.unit_label} × {money(l.unit_price)}</div>}
                  </div>
                  <span className="tabular-nums font-semibold text-sm">{money(l.quantity * l.unit_price)}</span>
                  <button data-testid="remove-line-btn" onClick={() => removeLine(i)} className="p-1.5 rounded hover:bg-destructive/10 transition-colors duration-200"><X className="h-4 w-4 text-muted-foreground" /></button>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Summary */}
        <div>
          <Card className="p-6 border-border rounded-lg bg-card sticky top-24">
            <div className="flex items-center gap-2 mb-4"><Calculator className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-bold tracking-tight">Quote Summary</h3></div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{money(subtotal)}</span></div>
              {mult !== 1 && <div className="flex justify-between"><span className="text-muted-foreground">{freqLabels[frequency]} ×{mult}</span><span className="tabular-nums">{money(adjusted)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({taxRate || 0}%)</span><span className="tabular-nums">{money(tax)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Est. cost</span><span className="tabular-nums">{money(cost)}</span></div>
              <div className="flex justify-between text-primary"><span>Profit</span><span className="tabular-nums">{money(profit)}</span></div>
              <div className="flex justify-between font-display font-black text-xl pt-2 border-t border-border"><span>Total</span><span data-testid="quote-total" className="tabular-nums">{money(total)}</span></div>
            </div>
            <Textarea placeholder="Notes for the customer…" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-4" />
            <div className="flex gap-2 mt-4">
              <Button variant="ghost" onClick={reset} className="flex-1">Reset</Button>
              <Button data-testid="save-quote-btn" onClick={save} className="flex-1 bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Quote</Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Saved quotes */}
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight mb-3">Saved Quotes</h3>
        <Card className="border-border rounded-lg overflow-hidden bg-card">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Quote #</TableHead><TableHead>Customer</TableHead><TableHead>Frequency</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {quotes.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No saved quotes yet.</TableCell></TableRow> : quotes.map((q) => (
                <TableRow key={q.id} data-testid="quote-row" className="hover:bg-muted/30 transition-colors duration-200">
                  <TableCell className="font-semibold">{q.quote_number}</TableCell>
                  <TableCell>{q.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{freqLabels[q.frequency] || q.frequency}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(q.total)}</TableCell>
                  <TableCell><Badge className={`capitalize border-0 ${statusStyle[q.status] || statusStyle.draft}`}>{q.status}</Badge>{q.converted_estimate_id && <Badge variant="outline" className="ml-1 text-[0.6rem] border-primary/30 text-primary">Estimate</Badge>}</TableCell>
                  <TableCell><div className="flex justify-end gap-1">
                    <button data-testid="print-quote-btn" title="Print / preview" onClick={() => openPrint(q.id)} className="p-2 rounded-md hover:bg-muted transition-colors duration-200"><Printer className="h-4 w-4 text-muted-foreground" /></button>
                    <button data-testid="share-quote-btn" title="Copy customer link" onClick={() => share(q.id)} className="p-2 rounded-md hover:bg-accent/10 transition-colors duration-200"><Share2 className="h-4 w-4 text-accent" /></button>
                    {!q.converted_estimate_id && <button data-testid="convert-estimate-btn" title="Convert to estimate" onClick={() => convert(q.id)} className="p-2 rounded-md hover:bg-primary/10 transition-colors duration-200"><FileCheck className="h-4 w-4 text-primary" /></button>}
                    <button data-testid="delete-quote-btn" onClick={() => remove(q.id)} className="p-2 rounded-md hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Add service dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Configure Service</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={svcId} onValueChange={selectService}>
                <SelectTrigger data-testid="config-service-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                <SelectContent>{catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {svc && (svc.variants || []).length > 0 && (
              <div className="space-y-1.5">
                <Label>{svc.name.includes("Mulch") ? "Color / Type" : "Option"}</Label>
                <Select value={String(variantIdx)} onValueChange={(v) => setVariantIdx(Number(v))}>
                  <SelectTrigger data-testid="config-variant-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{svc.variants.map((v, i) => <SelectItem key={i} value={String(i)}>{v.label} — {money(v.unit_price)}/{svc.unit}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {svc && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" /> Measurements</Label>
                <div className="grid grid-cols-3 gap-3">
                  {svc.measure_type === "area" && <>
                    <div><Label className="text-xs">Length (ft)</Label><Input data-testid="dim-length" type="number" value={dims.length} onChange={(e) => setDims({ ...dims, length: e.target.value })} /></div>
                    <div><Label className="text-xs">Width (ft)</Label><Input data-testid="dim-width" type="number" value={dims.width} onChange={(e) => setDims({ ...dims, width: e.target.value })} /></div>
                  </>}
                  {svc.measure_type === "volume" && <>
                    <div><Label className="text-xs">Length (ft)</Label><Input data-testid="dim-length" type="number" value={dims.length} onChange={(e) => setDims({ ...dims, length: e.target.value })} /></div>
                    <div><Label className="text-xs">Width (ft)</Label><Input data-testid="dim-width" type="number" value={dims.width} onChange={(e) => setDims({ ...dims, width: e.target.value })} /></div>
                    <div><Label className="text-xs">Depth (in)</Label><Input data-testid="dim-depth" type="number" value={dims.depth} onChange={(e) => setDims({ ...dims, depth: e.target.value })} /></div>
                  </>}
                  {svc.measure_type === "linear" && <div><Label className="text-xs">Length (ft)</Label><Input data-testid="dim-length" type="number" value={dims.length} onChange={(e) => setDims({ ...dims, length: e.target.value })} /></div>}
                  {(svc.measure_type === "count" || svc.measure_type === "flat") && <div><Label className="text-xs">Quantity</Label><Input data-testid="dim-quantity" type="number" value={dims.quantity} onChange={(e) => setDims({ ...dims, quantity: e.target.value })} /></div>}
                </div>
                <p className="text-xs text-muted-foreground">{detailStr(svc.measure_type, dims, qty, svc.unit)} @ {money(unitPrice)}/{svc.unit}</p>
              </div>
            )}

            {svc && (svc.addons || []).length > 0 && (
              <div className="space-y-2">
                <Label>Add-ons</Label>
                {svc.addons.map((a, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid="config-addon" checked={!!chosenAddons[i]} onCheckedChange={(v) => setChosenAddons({ ...chosenAddons, [i]: v })} />
                    <span className="flex-1">{a.label}</span><span className="tabular-nums text-muted-foreground">+{money(a.price)}</span>
                  </label>
                ))}
              </div>
            )}

            {svc && (
              <div className="rounded-md bg-primary/5 border border-primary/20 p-3 flex justify-between items-center">
                <span className="text-sm font-medium">Line total</span>
                <span className="font-display font-bold text-lg tabular-nums" data-testid="config-line-total">{money(previewTotal)}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="add-to-quote-btn" disabled={!svc} onClick={addToQuote} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Add to Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
